"use client";

import React from "react";
import { LocaleLink } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { track } from "@/lib/analytics";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/motion";
import { ArrowRight, Zap } from "lucide-react";

/**
 * eSIMCobra brutalist plans showcase — replaces the shared glass CombinedPlans.
 * Deal-first "Strike" cards (hard 2px borders, offset shadows, chartreuse/ink),
 * real catalog data via props. No glass.
 *
 * Market-test: `pricing_low_entry_high_upsell_v1` — "from $X" price-first cards
 * + plan-select rate (market-test-plan.md §3 Pricing display / Plan structure).
 */
const PRICING_EXPERIMENT_ID = "pricing_low_entry_high_upsell_v1";

const trackPlanClick = (code: string) => {
  try {
    track({
      event_name: "plan_card_clicked",
      event_id: `plan_card_clicked:${PRICING_EXPERIMENT_ID}:${code}:${Date.now()}`,
      experiment_id: PRICING_EXPERIMENT_ID,
      items: [{ item_id: `country:${code}`, item_category: "country", country: code }],
    });
  } catch {
    /* analytics is best-effort */
  }
};

export default function PlansShowcase({ destinations }: { destinations: { code: string; name: string; price?: string }[] }) {
  const { t } = useTranslations();
  const badges = ["HOT", "TOP", "DEAL", "FAST", "POPULAR", "VALUE"];

  React.useEffect(() => {
    try {
      track({
        event_name: "plans_showcase_viewed",
        event_id: `plans_showcase_viewed:${PRICING_EXPERIMENT_ID}`,
        experiment_id: PRICING_EXPERIMENT_ID,
      });
    } catch {
      /* analytics is best-effort */
    }
  }, []);

  const onPlanClick = (code: string) => {
    trackPlanClick(code);
  };

  return (
    <section className="container mx-auto px-4 py-14">
      <Reveal as="div" y={20} duration={0.4} className="flex items-end justify-between gap-4 mb-8 flex-wrap">
        <div>
          <span className="strike-eyebrow inline-flex items-center gap-2 border-2 border-foreground bg-warning text-warning-foreground px-3 py-1 shadow-[3px_3px_0_0_hsl(var(--foreground))]">
            <Zap className="w-3.5 h-3.5" /> {t("cobra.plans.eyebrow", "Best-selling plans")}
          </span>
          <h2 className="display-xl mt-4 text-3xl sm:text-4xl">{t("cobra.plans.title", "Strike a deal & go online")}</h2>
        </div>
        <LocaleLink href="/destinations/" className="inline-flex items-center gap-2 border-2 border-foreground bg-card text-foreground font-bold uppercase tracking-tight px-5 h-11 shadow-[3px_3px_0_0_hsl(var(--foreground))] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5 active:translate-x-0 active:translate-y-0">
          {t("cobra.plans.all", "All destinations")} <ArrowRight className="w-4 h-4" />
        </LocaleLink>
      </Reveal>

      <Stagger as="div" staggerChildren={0.05} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {destinations.map((d, i) => (
          <StaggerItem key={d.code} y={24} duration={0.35}>
            <LocaleLink
              href={`/destinations/${d.code}/`}
              onClick={() => onPlanClick(d.code)}
              className="group block border-2 border-foreground bg-card p-5 shadow-[5px_5px_0_0_hsl(var(--foreground))] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5 active:translate-x-0 active:translate-y-0"
            >
              <div className="flex items-center justify-between">
                <img src={`https://flagcdn.com/${d.code}.svg`} alt="" role="presentation" width={48} height={34} className="h-8 w-12 border-2 border-foreground" loading="lazy" referrerPolicy="no-referrer" />
                <span className="strike-eyebrow border-2 border-foreground bg-primary text-primary-foreground px-2 py-0.5 text-[10px]">{badges[i % badges.length]}</span>
              </div>
              <h3 className="mt-3 text-xl">{d.name}</h3>
              <div className="mt-1 text-sm font-bold normal-case text-foreground/70">{t("cobra.plans.coverage", "4G/5G · instant QR delivery")}</div>
              <div className="mt-4 flex items-center justify-between">
                <span className="display-xl text-2xl">{d.price ?? t("cobra.plans.from", "View")}</span>
                <span className="inline-flex items-center gap-1 font-bold uppercase text-sm">{t("cobra.plans.view", "Get plan")} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></span>
              </div>
            </LocaleLink>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}
